import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Row, Col, Form, Button, Card } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import * as GastosAPI from "../../api/gastos.api.ts";
import GastosFijosModal from "../../components/GastosFijosModal";
import Swal from 'sweetalert2';

export default function GastosRegistroPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams(); // Para detectar si estamos editando
    const navigate = useNavigate();
    const esEdicion = Boolean(id); // true si estamos editando, false si es nuevo
    
    const [formData, setFormData] = useState({
        anio_presupuesto: new Date().getFullYear(),
        tipo_id: '',
        categoria_id: '',
        subcategoria_id: '',
        descripcion: '',
        monto: '',
        fecha_gasto: '',
        responsable: '',
        fondo_id: '',
        documento_adjunto: '',
        observaciones: ''
    });

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    
    // Estados para datos de la API
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubcategorias] = useState([]);
    const [fondos, setFondos] = useState([]);
    const [tiposGastos, setTiposGastos] = useState([]);
    const [codigoPreview, setCodigoPreview] = useState('');
    const [showGastosFijosModal, setShowGastosFijosModal] = useState(false);

    // Función para mostrar alertas con SweetAlert2
    const mostrarAlerta = (mensaje, tipo = 'info', titulo = null) => {
        const config = {
            text: mensaje,
            confirmButtonText: 'Aceptar',
            allowOutsideClick: false,
            allowEscapeKey: true,
            showConfirmButton: true
        };

        switch (tipo) {
            case 'success':
                config.icon = 'success';
                config.title = titulo || '¡Éxito!';
                config.confirmButtonColor = '#28a745';
                break;
            case 'danger':
            case 'error':
                config.icon = 'error';
                config.title = titulo || 'Error';
                config.confirmButtonColor = '#dc3545';
                break;
            case 'warning':
                config.icon = 'warning';
                config.title = titulo || 'Advertencia';
                config.confirmButtonColor = '#ffc107';
                break;
            case 'info':
            default:
                config.icon = 'info';
                config.title = titulo || 'Información';
                config.confirmButtonColor = '#17a2b8';
                break;
        }

        return Swal.fire(config);
    };

    // Función para cargar fondos
    const cargarFondos = async (anio) => {
        try {
            const resp = await GastosAPI.getFondos(anio);
            if (resp.success && resp.data) {
                setFondos(resp.data);
            }
        } catch (error) {
            console.error('Error cargando fondos:', error);
        }
    };

    // Función para cargar preview de código
    const cargarPreviewCodigo = async (anio) => {
        try {
            const resp = await GastosAPI.previewCodigoGasto(anio);
            if (resp.success && resp.data) {
                setCodigoPreview(resp.data.codigo_preview);
            }
        } catch (error) {
            console.error('Error cargando preview de código:', error);
        }
    };

    // Función para cargar subcategorías filtradas
    const cargarSubcategorias = useCallback(async (idCategoria) => {
        try {
            const response = await GastosAPI.getSubcategorias(idCategoria);
            if (response.success && response.data) {
                setSubcategorias(response.data);
            }
        } catch (error) {
            console.error("Error al cargar subcategorías:", error);
        }
    }, []);

    // Función interna para cargar datos del gasto
    const cargarDatosGastoInterno = useCallback(async () => {
        if (!esEdicion || !id) return;
        
        try {
            const response = await GastosAPI.getGasto(id);
            
            if (response.success && response.data) {
                const gasto = response.data;
                
                // Primero cargar subcategorías si hay una categoría
                if (gasto.gas_categoria_id) {
                    await cargarSubcategorias(gasto.gas_categoria_id);
                }

                // Luego establecer todos los datos del formulario
                const nuevosFormData = {
                    anio_presupuesto: gasto.gas_anio_presupuesto || new Date().getFullYear(),
                    tipo_id: gasto.tipo_id ? String(gasto.tipo_id) : '',
                    categoria_id: gasto.gas_categoria_id ? String(gasto.gas_categoria_id) : '',
                    subcategoria_id: gasto.gas_subcategoria_id ? String(gasto.gas_subcategoria_id) : '',
                    descripcion: gasto.gas_descripcion || '',
                    monto: gasto.gas_monto || '',
                    fecha_gasto: gasto.gas_fecha_gasto ? gasto.gas_fecha_gasto.split('T')[0] : '',
                    responsable: gasto.gas_responsable || '',
                    fondo_id: gasto.gas_fondo_id ? String(gasto.gas_fondo_id) : '',
                    documento_adjunto: gasto.gas_documento_adjunto || '',
                    observaciones: gasto.gas_observaciones || ''
                };
                
                setFormData(nuevosFormData);

            } else {
                console.error('Error en respuesta de getGasto:', response);
                mostrarAlerta('No se pudo cargar la información del gasto.', 'error');
                navigate('/gastos');
            }
        } catch (error) {
            console.error('Error cargando datos del gasto:', error);
            mostrarAlerta('Error al cargar los datos del gasto.', 'error');
            navigate('/gastos');
        }
    }, [id, esEdicion, navigate, cargarSubcategorias]);

    // Función para cargar datos iniciales (sin dependencias de formData para evitar loops)
    const cargarDatosIniciales = useCallback(async () => {
        try {
            setLoadingData(true);
            
            // Usar el año actual por defecto
            const currentYear = new Date().getFullYear();
            let anioParaFondos = currentYear;
            
            // Si estamos editando, primero obtener el año del gasto
            if (esEdicion && id) {
                try {
                    const gastoResp = await GastosAPI.getGasto(id);
                    if (gastoResp.success && gastoResp.data) {
                        anioParaFondos = gastoResp.data.gas_anio_presupuesto || currentYear;
                    }
                } catch (error) {
                    console.error('Error obteniendo año del gasto:', error);
                }
            }

            const [categoriasResp, fondosResp, tiposResp] = await Promise.all([
                GastosAPI.getCategorias(),
                GastosAPI.getFondos(anioParaFondos),
                GastosAPI.getTiposGastos()
            ]);

            if (categoriasResp.success && categoriasResp.data) {
                setCategorias(categoriasResp.data);
            }

            if (fondosResp.success && fondosResp.data) {
                setFondos(fondosResp.data);
            }

            if (tiposResp.success && tiposResp.data) {
                setTiposGastos(tiposResp.data);
            }

            // Cargar preview de código solo si no estamos editando
            if (!esEdicion) {
                await cargarPreviewCodigo(currentYear);
            }

            // Si estamos en modo edición, cargar los datos del gasto DESPUÉS de cargar las opciones
            if (esEdicion && id) {
                await cargarDatosGastoInterno();
            }

        } catch (error) {
            console.error('Error cargando datos iniciales:', error);
            mostrarAlerta(
                'No se pudieron cargar los datos necesarios para el formulario. Por favor, recargue la página.', 
                'error', 
                'Error de Carga'
            );
        } finally {
            setLoadingData(false);
        }
    }, [esEdicion, id, cargarDatosGastoInterno]);

    // Cargar datos iniciales solo una vez al montar
    useEffect(() => {
        cargarDatosIniciales();
    }, [cargarDatosIniciales]);

    // Cargar fondos cuando cambia el año
    useEffect(() => {
        if (formData.anio_presupuesto) {
            cargarFondos(formData.anio_presupuesto);
            cargarPreviewCodigo(formData.anio_presupuesto);
        }
    }, [formData.anio_presupuesto]);

    // Generar años disponibles (memoizado para evitar recálculo en cada render)
    const aniosDisponibles = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= currentYear - 4; i--) {
            years.push(i);
        }
        return years;
    }, []);

    // Manejador de cambios de input (memoizado)
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    // Manejador de cambio de categoría (memoizado)
    const handleCategoriaChange = useCallback(async (e) => {
        const categoriaId = e.target.value;
        
        setFormData(prev => ({
            ...prev,
            categoria_id: categoriaId,
            subcategoria_id: '' // Limpiar subcategoría al cambiar categoría
        }));

        if (categoriaId) {
            await cargarSubcategorias(categoriaId);
        } else {
            setSubcategorias([]);
        }
    }, [cargarSubcategorias]);

    // Función para imprimir comprobante del gasto (solo en modo edición)
    const imprimirComprobante = async () => {
        if (!esEdicion || !id) return;
        
        try {
            // Obtener datos actualizados del gasto
            const response = await GastosAPI.getGasto(id);
            if (!response.success || !response.data) {
                mostrarAlerta('No se pudo obtener los datos del gasto para imprimir', 'error');
                return;
            }
            
            const gasto = response.data;
            
            // Función para formatear moneda
            const formatCurrency = (amount) => {
                const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
                return new Intl.NumberFormat('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0
                }).format(numAmount);
            };
            
            // Función para formatear fecha
            const formatDate = (dateString) => {
                if (!dateString) return 'No especificada';
                const date = new Date(dateString);
                return date.toLocaleDateString('es-CL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            };
            
            // Crear ventana de impresión
            const ventanaImpresion = window.open('', '_blank', 'width=800,height=600');
            
            const contenidoComprobante = `
                <!DOCTYPE html>
                <html lang="es">
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>Comprobante de Gasto - ${gasto.gas_codigo}</title>
                    <style>
                        body {
                            font-family: 'Arial', sans-serif;
                            margin: 20px;
                            color: #333;
                            line-height: 1.6;
                        }
                        .header {
                            text-align: center;
                            border-bottom: 3px solid #007bff;
                            padding-bottom: 20px;
                            margin-bottom: 30px;
                        }
                        .header h1 {
                            color: #007bff;
                            margin: 0;
                            font-size: 28px;
                        }
                        .header h2 {
                            color: #666;
                            margin: 5px 0 0 0;
                            font-size: 18px;
                            font-weight: normal;
                        }
                        .comprobante-info {
                            display: flex;
                            justify-content: space-between;
                            margin-bottom: 30px;
                            background-color: #f8f9fa;
                            padding: 15px;
                            border-radius: 8px;
                        }
                        .info-section {
                            display: grid;
                            grid-template-columns: repeat(2, 1fr);
                            gap: 20px;
                            margin-bottom: 30px;
                        }
                        .info-group {
                            border: 1px solid #e9ecef;
                            padding: 15px;
                            border-radius: 6px;
                            background-color: #fff;
                        }
                        .info-label {
                            font-weight: bold;
                            color: #495057;
                            font-size: 12px;
                            text-transform: uppercase;
                            margin-bottom: 5px;
                            display: block;
                        }
                        .info-value {
                            font-size: 14px;
                            color: #212529;
                        }
                        .monto-section {
                            text-align: center;
                            background: linear-gradient(135deg, #28a745, #20c997);
                            color: white;
                            padding: 20px;
                            border-radius: 10px;
                            margin: 30px 0;
                        }
                        .monto-value {
                            font-size: 36px;
                            font-weight: bold;
                            margin: 0;
                        }
                        .monto-label {
                            font-size: 16px;
                            opacity: 0.9;
                            margin: 5px 0 0 0;
                        }
                        .status-badge {
                            display: inline-block;
                            padding: 6px 12px;
                            border-radius: 20px;
                            font-size: 12px;
                            font-weight: bold;
                            text-transform: uppercase;
                        }
                        .status-aprobado { background-color: #d4edda; color: #155724; }
                        .status-pendiente { background-color: #fff3cd; color: #856404; }
                        .status-rechazado { background-color: #f8d7da; color: #721c24; }
                        .status-borrador { background-color: #e2e3e5; color: #383d41; }
                        .footer {
                            margin-top: 40px;
                            text-align: center;
                            font-size: 12px;
                            color: #6c757d;
                            border-top: 1px solid #e9ecef;
                            padding-top: 20px;
                        }
                        .categoria-indicator {
                            width: 20px;
                            height: 20px;
                            border-radius: 4px;
                            display: inline-block;
                            margin-right: 8px;
                            vertical-align: middle;
                        }
                        @media print {
                            body { margin: 0; }
                            .no-print { display: none; }
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>COMPROBANTE DE GASTO</h1>
                        <h2>Sistema de Gestión Cristour</h2>
                    </div>

                    <div class="comprobante-info">
                        <div>
                            <strong>Código:</strong> ${gasto.gas_codigo}<br>
                            <strong>Estado:</strong> 
                            <span class="status-badge status-${gasto.gas_estado?.toLowerCase() || 'borrador'}">
                                ${gasto.gas_estado || 'BORRADOR'}
                            </span>
                        </div>
                        <div style="text-align: right;">
                            <strong>Fecha de emisión:</strong><br>
                            ${new Date().toLocaleDateString('es-CL', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </div>
                    </div>

                    <div class="monto-section">
                        <p class="monto-value">${formatCurrency(gasto.gas_monto)}</p>
                        <p class="monto-label">Monto del Gasto</p>
                    </div>

                    <div class="info-section">
                        <div class="info-group">
                            <span class="info-label">Descripción</span>
                            <div class="info-value">${gasto.gas_descripcion}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Responsable</span>
                            <div class="info-value">${gasto.gas_responsable}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Categoría</span>
                            <div class="info-value">
                                <span class="categoria-indicator" style="background-color: ${gasto.categoria_color || '#6c757d'};"></span>
                                ${gasto.categoria_nombre || 'No especificada'}
                            </div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Subcategoría</span>
                            <div class="info-value">${gasto.subcategoria_nombre || 'No especificada'}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Tipo de Gasto</span>
                            <div class="info-value">${gasto.tipo_nombre || 'No especificado'}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Fondo</span>
                            <div class="info-value">${gasto.fondo_nombre || 'Sin fondo asignado'}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Fecha del Gasto</span>
                            <div class="info-value">${formatDate(gasto.gas_fecha_gasto)}</div>
                        </div>
                        
                        <div class="info-group">
                            <span class="info-label">Año Presupuesto</span>
                            <div class="info-value">${gasto.gas_anio_presupuesto}</div>
                        </div>
                    </div>

                    ${gasto.gas_observaciones ? `
                        <div class="info-group" style="grid-column: 1 / -1;">
                            <span class="info-label">Observaciones</span>
                            <div class="info-value">${gasto.gas_observaciones}</div>
                        </div>
                    ` : ''}

                    <div class="footer">
                        <p>Este comprobante es un documento informativo generado automáticamente por el sistema.</p>
                        <p>Fecha de creación del gasto: ${formatDate(gasto.gas_fecha_creacion)}</p>
                    </div>

                    <div class="no-print" style="text-align: center; margin-top: 30px;">
                        <button onclick="window.print()" style="
                            background-color: #007bff;
                            color: white;
                            border: none;
                            padding: 12px 24px;
                            border-radius: 6px;
                            font-size: 16px;
                            cursor: pointer;
                            margin-right: 10px;
                        ">Imprimir</button>
                        <button onclick="window.close()" style="
                            background-color: #6c757d;
                            color: white;
                            border: none;
                            padding: 12px 24px;
                            border-radius: 6px;
                            font-size: 16px;
                            cursor: pointer;
                        ">Cerrar</button>
                    </div>
                </body>
                </html>
            `;
            
            ventanaImpresion.document.write(contenidoComprobante);
            ventanaImpresion.document.close();
            ventanaImpresion.focus();
            
        } catch (error) {
            console.error('Error al imprimir comprobante:', error);
            mostrarAlerta('Error al generar el comprobante de impresión', 'error');
        }
    };

    // handleSubmit optimizado con useCallback
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        // Validaciones más específicas
        const erroresValidacion = [];
        
        if (!formData.tipo_id) erroresValidacion.push('Tipo de gasto es obligatorio');
        if (!formData.categoria_id) erroresValidacion.push('Categoría es obligatoria');
        if (!formData.subcategoria_id) erroresValidacion.push('Subcategoría es obligatoria');
        if (!formData.descripcion) erroresValidacion.push('Descripción es obligatoria');
        if (!formData.monto) erroresValidacion.push('Monto es obligatorio');
        if (!formData.fecha_gasto) erroresValidacion.push('Fecha de gasto es obligatoria');
        if (!formData.responsable) erroresValidacion.push('Responsable es obligatorio');
        
        if (erroresValidacion.length > 0) {
            mostrarAlerta(
                `Por favor complete los siguientes campos:\n\n• ${erroresValidacion.join('\n• ')}`, 
                'warning', 
                'Campos Obligatorios'
            );
            return;
        }

        if (parseFloat(formData.monto) <= 0) {
            mostrarAlerta('El monto debe ser mayor a cero', 'warning', 'Monto Inválido');
            return;
        }

        // Validar que la fecha no sea futura
        const fechaGasto = new Date(formData.fecha_gasto);
        const hoy = new Date();
        hoy.setHours(23, 59, 59, 999); // Permitir hasta el final del día actual
        
        if (fechaGasto > hoy) {
            mostrarAlerta(
                'La fecha del gasto no puede ser posterior a la fecha actual.', 
                'warning', 
                'Fecha Inválida'
            );
            return;
        }

        // Validar que la fecha corresponda al año presupuestario
        const anioFecha = fechaGasto.getFullYear();
        if (anioFecha !== parseInt(formData.anio_presupuesto)) {
            mostrarAlerta(
                `La fecha del gasto debe corresponder al año presupuestario seleccionado (${formData.anio_presupuesto}).`, 
                'warning', 
                'Año Presupuestario Incorrecto'
            );
            return;
        }

        try {
            setLoading(true);
            
            const gastoData = {
                anio_presupuesto: parseInt(formData.anio_presupuesto),
                tipo_id: parseInt(formData.tipo_id),
                categoria_id: parseInt(formData.categoria_id),
                subcategoria_id: parseInt(formData.subcategoria_id),
                descripcion: formData.descripcion,
                monto: parseFloat(formData.monto),
                fecha_gasto: formData.fecha_gasto,
                responsable: formData.responsable,
                fondo_id: formData.fondo_id ? parseInt(formData.fondo_id) : null,
                documento_adjunto: formData.documento_adjunto || null,
                observaciones: formData.observaciones || null
            };

            let resultado;
            if (esEdicion) {
                resultado = await GastosAPI.actualizarGasto(id, gastoData);
            } else {
                resultado = await GastosAPI.crearGasto(gastoData);
            }
            
            if (resultado.success) {
                await mostrarAlerta(
                    resultado.message || (esEdicion ? 'El gasto ha sido actualizado exitosamente.' : 'El gasto ha sido registrado exitosamente.'), 
                    'success', 
                    esEdicion ? 'Gasto Actualizado' : 'Gasto Registrado'
                );
                
                if (esEdicion) {
                    // En modo edición, redirigir a la vista del gasto
                    navigate(`/gastos/ver/${id}`);
                } else {
                    // En modo creación, reset form
                    setFormData({
                        anio_presupuesto: new Date().getFullYear(),
                        tipo_id: '',
                        categoria_id: '',
                        subcategoria_id: '',
                        descripcion: '',
                        monto: '',
                        fecha_gasto: '',
                        responsable: '',
                        fondo_id: '',
                        documento_adjunto: '',
                        observaciones: ''
                    });

                    await cargarPreviewCodigo(formData.anio_presupuesto);
                }
            } else {
                // Mostrar mensaje específico del servidor
                let errorMessage = resultado.message || (esEdicion ? 'No se pudo actualizar el gasto.' : 'No se pudo registrar el gasto.');
                
                // Si hay errores de validación específicos, incluirlos
                if (resultado.errors && Array.isArray(resultado.errors)) {
                    errorMessage += '\n\nDetalles:\n• ' + resultado.errors.join('\n• ');
                }
                
                mostrarAlerta(errorMessage, 'error', esEdicion ? 'Error al Actualizar Gasto' : 'Error al Registrar Gasto');
            }
        } catch (error) {
            console.error('Error al enviar formulario:', error);
            
            // Manejo más específico de errores
            let errorMessage = esEdicion ? 'Ocurrió un error inesperado al actualizar el gasto.' : 'Ocurrió un error inesperado al registrar el gasto.';
            let titulo = 'Error de Conexión';
            
            if (error.response) {
                // Error del servidor con respuesta HTTP
                titulo = `Error del Servidor (${error.response.status})`;
                if (error.response.data) {
                    errorMessage = error.response.data.message || error.response.data.error || errorMessage;
                    
                    // Si hay errores de validación específicos
                    if (error.response.data.errors) {
                        const errorsText = Array.isArray(error.response.data.errors) 
                            ? error.response.data.errors.join('\n• ')
                            : error.response.data.errors;
                        errorMessage += '\n\nDetalles:\n• ' + errorsText;
                    }
                } else {
                    errorMessage = `Error del servidor. Código: ${error.response.status}`;
                }
            } else if (error.request) {
                // Error de red/conexión
                titulo = 'Sin Conexión';
                errorMessage = 'No se pudo conectar con el servidor. Verifique su conexión a internet e intente nuevamente.';
            } else if (error.message) {
                // Otros errores
                errorMessage = error.message;
            }
            
            mostrarAlerta(errorMessage, 'error', titulo);
        } finally {
            setLoading(false);
        }
    }, [esEdicion, id, formData, navigate]);

    if (loadingData) {
        return (
            <PageLayout>
                <div className="d-flex justify-content-center">
                    <div>Cargando datos...</div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mt-3">
                        <Card.Header className="border-0 align-items-center d-flex">
                            <h4 className="card-title mb-0 flex-grow-1">
                                {esEdicion ? "Editar Gasto" : t("Registro de Gastos")}
                            </h4>
                            <div className="d-flex gap-2">
                                {!esEdicion && (
                                    <Button 
                                        variant="success" 
                                        size="sm"
                                        onClick={() => setShowGastosFijosModal(true)}
                                        className="d-flex align-items-center"
                                    >
                                        <i className="fas fa-money-bill-wave me-2"></i>
                                        Gastos Fijos
                                    </Button>
                                )}
                                <Link to="/gastos" className="btn btn-outline-primary btn-sm">
                                    Ver Gastos
                                </Link>
                            </div>
                        </Card.Header>

                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                {/* Información Básica */}
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h5 className="mb-0">Información Básica</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Año Presupuesto *</Form.Label>
                                                    <Form.Select
                                                        name="anio_presupuesto"
                                                        value={formData.anio_presupuesto}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        {aniosDisponibles.map(year => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Tipo de Gasto *</Form.Label>
                                                    <Form.Select
                                                        name="tipo_id"
                                                        value={formData.tipo_id}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="">Seleccione tipo...</option>
                                                        {tiposGastos.map((tipo) => (
                                                            <option key={tipo.tipo_id} value={tipo.tipo_id}>
                                                                {tipo.tipo_nombre}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Categoría *</Form.Label>
                                                    <Form.Select
                                                        name="categoria_id"
                                                        value={formData.categoria_id}
                                                        onChange={handleCategoriaChange}
                                                        required
                                                    >
                                                        <option value="">Seleccione categoría...</option>
                                                        {categorias.map((categoria) => (
                                                            <option key={categoria.cat_id} value={categoria.cat_id}>
                                                                {categoria.cat_nombre}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Subcategoría *</Form.Label>
                                                    <Form.Select
                                                        name="subcategoria_id"
                                                        value={formData.subcategoria_id}
                                                        onChange={handleInputChange}
                                                        required
                                                        disabled={!formData.categoria_id}
                                                    >
                                                        <option value="">Seleccione subcategoría...</option>
                                                        {subcategorias.map((subcategoria) => (
                                                            <option key={subcategoria.sub_id} value={subcategoria.sub_id}>
                                                                {subcategoria.sub_nombre}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Descripción *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="descripcion"
                                                value={formData.descripcion}
                                                onChange={handleInputChange}
                                                placeholder="Describa el gasto..."
                                                required
                                            />
                                        </Form.Group>
                                    </Card.Body>
                                </Card>

                                {/* Detalles Financieros */}
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h5 className="mb-0">Detalles Financieros</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Monto *</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="monto"
                                                        value={formData.monto}
                                                        onChange={handleInputChange}
                                                        placeholder="0.00"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Fecha del Gasto *</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="fecha_gasto"
                                                        value={formData.fecha_gasto}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Fondo</Form.Label>
                                                    <Form.Select
                                                        name="fondo_id"
                                                        value={formData.fondo_id}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Sin fondo específico</option>
                                                        {fondos.map((fondo) => (
                                                            <option key={fondo.fon_id} value={fondo.fon_id}>
                                                                {fondo.fon_nombre} - ${Number(fondo.fon_presupuesto_anual).toLocaleString()}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Información Adicional */}
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h5 className="mb-0">Información Adicional</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Responsable *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="responsable"
                                                        value={formData.responsable}
                                                        onChange={handleInputChange}
                                                        placeholder="Nombre del responsable"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Documento Adjunto</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="documento_adjunto"
                                                        value={formData.documento_adjunto}
                                                        onChange={handleInputChange}
                                                        placeholder="URL o nombre del documento"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Observaciones</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="observaciones"
                                                value={formData.observaciones}
                                                onChange={handleInputChange}
                                                placeholder="Observaciones adicionales..."
                                            />
                                        </Form.Group>
                                    </Card.Body>
                                </Card>

                                {/* Preview del Código */}
                                {codigoPreview && (
                                    <Card className="mb-3">
                                        <Card.Header>
                                            <h5 className="mb-0">Código de Gasto Siguiente</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="alert alert-info d-flex align-items-center">
                                                <i className="material-icons me-2">info</i>
                                                <div>
                                                    <strong>Próximo código:</strong> {codigoPreview}
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Botones */}
                                <div className="d-flex justify-content-end gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        type="button"
                                        onClick={() => window.history.back()}
                                    >
                                        Cancelar
                                    </Button>
                                    
                                    {esEdicion && (
                                        <Button
                                            variant="info"
                                            type="button"
                                            onClick={imprimirComprobante}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-print me-2"></i>
                                            Imprimir Comprobante
                                        </Button>
                                    )}
                                    
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={loading}
                                    >
                                        {loading ? 
                                            (esEdicion ? 'Actualizando...' : 'Registrando...') : 
                                            (esEdicion ? 'Actualizar Gasto' : 'Registrar Gasto')
                                        }
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal de Gastos Fijos */}
            <GastosFijosModal
                show={showGastosFijosModal}
                onHide={() => setShowGastosFijosModal(false)}
                onGastoCreado={(nuevoGasto) => {
                    setShowGastosFijosModal(false);
                    Swal.fire({
                        icon: 'success',
                        title: 'Gasto creado',
                        text: 'El gasto ha sido registrado exitosamente desde la plantilla',
                        timer: 2000
                    });
                    // Opcional: navegar a la vista del gasto
                    if (nuevoGasto && nuevoGasto.gas_id) {
                        navigate(`/gastos/ver/${nuevoGasto.gas_id}`);
                    }
                }}
            />
        </PageLayout>
    );
}